import { observer } from 'mobx-react';
import { autoinject, inject } from 'Mri.Web.Common';
import * as React from 'react';
import { formatMetric, formatPercent, MetricChart, MetricKind } from './MetricChart';
import { MetricSeries, PodChartsStore, RANGE_OPTIONS } from './PodChartsStore';

@autoinject
@observer
export class PodChartsDashboard extends React.Component {
    public async componentDidMount() {
        await this.podChartsStore.loadAsync();
    }

    public render() {
        const { metrics, loading, loaded, error } = this.podChartsStore;

        return (
            <div className="container-fluid p-3">
                {this.renderHeader()}

                {error && (
                    <div className="alert alert-danger small">
                        <strong>Could not load pod metrics.</strong> {error}
                    </div>
                )}

                {this.renderCollectionNote()}

                {loaded && !error && !metrics.available && (
                    <div className="alert alert-warning small">
                        <strong>No pod metrics are being collected.</strong>{' '}
                        {metrics.unavailableReason}
                    </div>
                )}

                {this.renderNodes()}
                {this.renderPods()}

                {metrics.available && metrics.pods.length === 0 && !loading && (
                    <div className="alert alert-secondary small">
                        No pods have been sampled yet. The first samples appear within{' '}
                        {metrics.sampleIntervalSeconds} seconds of this page's service starting.
                    </div>
                )}
            </div>
        );
    }

    private renderHeader() {
        const { hours, loading } = this.podChartsStore;

        return (
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <h4 className="mb-0">Pod Charts</h4>
                <div className="d-flex align-items-center gap-2">
                    <div className="btn-group btn-group-sm" role="group">
                        {RANGE_OPTIONS.map(option => (
                            <button
                                key={option.hours}
                                type="button"
                                className={`btn ${hours === option.hours ? 'btn-primary' : 'btn-outline-secondary'}`}
                                onClick={() => this.podChartsStore.setHours(option.hours)}
                                disabled={loading}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                    <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={this.handleRefresh}
                        disabled={loading}
                    >
                        {loading ? 'Refreshing...' : 'Refresh'}
                    </button>
                </div>
            </div>
        );
    }

    /** The window is only as long as the collector has been up — say so, so a short chart after a deploy
     *  is read as "we have not been collecting that long", not as a gap in the cluster. */
    private renderCollectionNote() {
        const { metrics } = this.podChartsStore;

        // `available` is the POD flag. Gating on it alone hid this note whenever pod sampling failed
        // while node sampling kept working — leaving the controller charts drawn with no "History
        // starts ..." caveat beside them, which is the misreading the note exists to prevent.
        if (!metrics.available && !metrics.nodesAvailable) {
            return null;
        }

        const oldest = metrics.oldestSample ? new Date(metrics.oldestSample) : null;
        const collectingFor = oldest ? Date.now() - oldest.getTime() : 0;
        const hoursCollected = collectingFor / (1000 * 60 * 60);
        const short = oldest != null && hoursCollected < metrics.retentionHours - 1;

        return (
            <div className="alert alert-secondary small">
                <div className="mb-1">
                    Sampled from the cluster's metrics API every{' '}
                    <strong>{metrics.sampleIntervalSeconds} seconds</strong> and kept for{' '}
                    <strong>{Math.round(metrics.retentionHours / 24)} days</strong>.
                </div>
                <div className="mb-1">
                    <strong>Reading these numbers.</strong> Each figure is the share of that pod's own
                    declared limit — <em>33% of 2 cores</em> means the pod may use 2 CPU cores and is
                    currently using a third of them. Not every pod declares a limit: those show their
                    current usage on its own (<em>0.047 cores</em>) with no percentage, because there is
                    no ceiling for it to be a percentage <em>of</em>.
                </div>
                <div className="mb-1">
                    <strong>CPU is measured in cores.</strong> Kubernetes reports it in{' '}
                    <strong>millicores</strong> (written <code>m</code>) — a millicore is one thousandth of
                    a CPU core, so <code>1000m</code> is one full core, <code>500m</code> is half a core,
                    and <code>1m</code> is an essentially idle pod. This page converts that to cores, and
                    each chart's tooltip also gives the raw millicore figure, which is the unit{' '}
                    <code>kubectl top</code> prints.
                </div>
                <div className="mb-1">
                    <strong>The figure beside each chart is the newest sample</strong> — that is the one to
                    compare against <code>kubectl top</code>. The plotted line is averaged into buckets to
                    keep a week of per-minute samples drawable, so over the wider ranges a point is a mean of
                    the minutes around it and a brief spike shows up flattened.
                </div>
                {short && (
                    <div className="mb-1">
                        <strong>History starts {oldest!.toLocaleString()}</strong> — that is when this service
                        last started. The samples live in the service's memory, not a database, so restarting
                        or scaling the internal pod (including from the Infrastructure page) begins the window
                        again.
                    </div>
                )}
                {!metrics.nodesAvailable && (
                    <div className="mb-0">
                        <strong>Controller charts unavailable.</strong> {metrics.nodesUnavailableReason}
                    </div>
                )}
            </div>
        );
    }

    private renderNodes() {
        const { metrics } = this.podChartsStore;

        if (metrics.nodes.length === 0) {
            return null;
        }

        return (
            <div className="mb-4">
                <h5 className="mb-2">Controllers</h5>
                <div className="row g-3">
                    {metrics.nodes.map(node => (
                        <div className="col-12 col-xl-6" key={node.name}>
                            {this.renderSeriesCard(node, 'Node')}
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    private renderPods() {
        const { podsByApp } = this.podChartsStore;

        if (podsByApp.length === 0) {
            return null;
        }

        return (
            <>
                <h5 className="mb-2">Pods</h5>
                {podsByApp.map(group => (
                    <div className="mb-4" key={group.app}>
                        <div className="text-muted small text-uppercase fw-bold mb-2">{group.app}</div>
                        <div className="row g-3">
                            {group.pods.map(pod => (
                                <div className="col-12 col-xl-6" key={pod.name}>
                                    {this.renderSeriesCard(pod, pod.node)}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </>
        );
    }

    private renderSeriesCard(series: MetricSeries, subtitle: string | null) {
        const { from, to } = this.podChartsStore.windowFor(series);

        return (
            <div className="card h-100">
                <div className="card-header py-2">
                    <div className="fw-bold text-break">{series.name}</div>
                    {subtitle && <div className="text-muted small">{subtitle}</div>}
                </div>
                <div className="card-body p-2">
                    {series.points.length === 0 ? (
                        <div className="text-muted small p-2">No samples in this window.</div>
                    ) : (
                        <div className="row g-2">
                            {this.renderMetric(series, 'cpu', 'CPU', series.cpuCapacity, from, to)}
                            {this.renderMetric(series, 'memory', 'Memory', series.memoryCapacity, from, to)}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    private renderMetric(
        series: MetricSeries,
        kind: MetricKind,
        label: string,
        capacity: number | null,
        from: Date,
        to: Date
    ) {
        // series.latest is the newest RAW sample; the last plotted point is a bucket average over as much
        // as half an hour at wide ranges, which would understate a pod that just spiked.
        const latest = series.latest ?? series.points[series.points.length - 1];
        const value = latest ? latest[kind] : 0;
        const percent = capacity && capacity > 0 ? (value / capacity) * 100 : null;

        return (
            <div className="col-12 col-md-6">
                <div className="d-flex justify-content-between align-items-baseline mb-1">
                    <span className="small fw-bold">{label}</span>
                    <span className="small text-muted">
                        {/* No declared limit means no denominator. Any percentage here would be against
                            a baseline we invented (node capacity? the cluster?), so show the plain
                            reading instead — the note above the charts explains why some cards differ. */}
                        {percent != null
                            ? `${formatPercent(percent)} of ${formatMetric(kind, capacity!)}`
                            : formatMetric(kind, value)}
                    </span>
                </div>
                <MetricChart
                    points={series.points}
                    kind={kind}
                    capacity={capacity}
                    from={from}
                    to={to}
                />
            </div>
        );
    }

    private handleRefresh = async () => {
        await this.podChartsStore.loadAsync();
    };

    // Property name is the lookup key into the store container — it must match stores.tsx.
    @inject
    private podChartsStore!: PodChartsStore;
}
